import { useState, useCallback, useMemo } from 'react';
import { Header } from './components/Header';
import { OptionsPanel } from './components/OptionsPanel';
import { PreviewPanel } from './components/PreviewPanel';
import { useFileHandler } from './hooks/useFileHandler';
import { useConverter } from './hooks/useConverter';
import { markdownToHtml } from './utils/render';

/**
 * Main Application Component
 */
function App() {
  const [previewMode, setPreviewMode] = useState('markdown'); // 'markdown' | 'html'
  const [options, setOptions] = useState({
    includeFrontmatter: true,
    includeRunSettings: true,
    includeSystemInstruction: true,
    includeHiddenThoughts: false,
    includeGrounding: false,
    collapseWhitespace: true,
    wrapWidth: 0,
    headingStyle: "atx",
    roleHeadings: true,
  });

  const {
    rawJson,
    error: fileError,
    fileName,
    fileInputRef,
    handleFile,
    handleDrop,
    handleDragOver,
    openFileDialog,
    clearFile,
  } = useFileHandler();

  const { md, stats, error: conversionError } = useConverter(rawJson, options);
  const html = useMemo(() => (md ? markdownToHtml(md) : ''), [md]);

  const toggleOption = useCallback((key) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setNumOption = useCallback((key, value) => {
    const n = Number(value);
    setOptions((prev) => ({ ...prev, [key]: Number.isFinite(n) ? n : 0 }));
  }, []);

  const setTextOption = useCallback((key, value) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const copyToClipboard = useCallback(() => {
    const text = previewMode === 'html' ? html : md;
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => {
        // Optional: Show success toast
        console.log('Copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }, [previewMode, md, html]);

  const downloadMarkdown = useCallback(() => {
    if (!md) return;
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "conversation.md";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [md, fileName]);

  const hasContent = Boolean(rawJson.trim());
  const error = fileError || conversionError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header
        onOpenFile={openFileDialog}
        onCopy={copyToClipboard}
        onDownload={downloadMarkdown}
        onClear={clearFile}
        hasContent={hasContent}
        fileInputRef={fileInputRef}
        onFileChange={handleFile}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Options Panel */}
          <aside className="lg:col-span-1">
            <OptionsPanel
              options={options}
              onToggle={toggleOption}
              onSetNum={setNumOption}
              onSetText={setTextOption}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onOpenFile={openFileDialog}
              error={fileError}
              stats={stats}
              hasData={hasContent}
            />
          </aside>

          {/* Preview Panel */}
          <section className="lg:col-span-2">
            <PreviewPanel
              markdown={md}
              html={html}
              mode={previewMode}
              onChangeMode={setPreviewMode}
              error={conversionError}
              hasData={hasContent}
            />
          </section>
        </div>
      </main>

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <p className="text-xs text-slate-500">
          Built for converting Google AI Studio JSON exports to Markdown.{' '}
          <span className="font-medium">All processing happens in your browser</span> — no data is sent to any server.
        </p>
      </footer>
    </div>
  );
}

export default App;
